import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { Remediation } from '@shared/types';
import {
  getSecurityFinding,
  markRemediationCompleted,
  type SecurityFindingDetail,
} from '../lib/api-client';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-green-100 text-green-800',
};

export function FindingDetail() {
  const { id } = useParams<{ id: string }>();
  const [finding, setFinding] = useState<SecurityFindingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completingId, setCompletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    async function fetchFinding() {
      setLoading(true);
      setError(null);
      try {
        const res = await getSecurityFinding(id!);
        setFinding(res.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar el hallazgo');
      } finally {
        setLoading(false);
      }
    }

    fetchFinding();
  }, [id]);

  async function handleMarkCompleted(remediation: Remediation) {
    if (!finding) return;
    setCompletingId(remediation.id);
    try {
      await markRemediationCompleted(finding.id, remediation.id);
      setFinding((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          remediations: prev.remediations.map((r) =>
            r.id === remediation.id
              ? { ...r, status: 'completed' as const, completedAt: new Date().toISOString() }
              : r
          ),
        };
      });
    } catch {
      // Silently handle — user can retry
    } finally {
      setCompletingId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !finding) {
    return (
      <div className="rounded-lg bg-red-50 p-6 text-center">
        <p className="text-red-700">{error || 'Hallazgo no encontrado'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hallazgo de Seguridad</h1>
          <p className="mt-1 text-sm text-gray-500">ID: {finding.id}</p>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
            SEVERITY_COLORS[finding.severity ?? ''] ?? 'bg-gray-100 text-gray-800'
          }`}
        >
          {(finding.severity ?? 'unknown').toUpperCase()}
        </span>
      </div>

      {/* Finding Details */}
      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Detalles del Hallazgo</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Recurso Afectado
            </dt>
            <dd className="mt-0.5 text-sm">
              <Link
                to={`/inventory/${finding.resourceId}`}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                {finding.resourceName || finding.resourceId}
              </Link>
              {finding.resourceType && (
                <span className="ml-2 text-xs text-gray-500">
                  ({finding.resourceType.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')})
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Proyecto
            </dt>
            <dd className="mt-0.5 text-sm text-gray-900">{finding.projectOrAccount || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Regla Violada
            </dt>
            <dd className="mt-0.5 text-sm text-gray-900">{finding.ruleId}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Dirección
            </dt>
            <dd className="mt-0.5 text-sm text-gray-900">
              {finding.direction ? (
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                  {finding.direction}
                </span>
              ) : (
                <span className="text-gray-500">—</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Tipo de Exposición
            </dt>
            <dd className="mt-0.5 text-sm text-gray-900">
              {finding.exposureType ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Región
            </dt>
            <dd className="mt-0.5 text-sm text-gray-900">{finding.region || 'Global'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Puertos
            </dt>
            <dd className="mt-0.5 text-sm text-gray-900">
              {finding.ports && finding.ports.length > 0 ? (
                <span className="flex flex-wrap gap-1">
                  {finding.ports.map((port: string) => (
                    <span
                      key={port}
                      className="inline-flex items-center rounded bg-red-50 px-2 py-0.5 text-xs font-mono text-red-700"
                    >
                      {port}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="text-gray-500">—</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Protocolos
            </dt>
            <dd className="mt-0.5 text-sm text-gray-900">
              {finding.protocols && finding.protocols.length > 0 ? (
                <span className="flex flex-wrap gap-1">
                  {finding.protocols.map((protocol: string) => (
                    <span
                      key={protocol}
                      className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-mono text-gray-700"
                    >
                      {protocol}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="text-gray-500">—</span>
              )}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Rangos IP Expuestos
            </dt>
            <dd className="mt-0.5 text-sm text-gray-900">
              {finding.ipRanges && finding.ipRanges.length > 0 ? (
                <span className="flex flex-wrap gap-1">
                  {finding.ipRanges.map((range: string) => (
                    <span
                      key={range}
                      className="inline-flex items-center rounded bg-orange-50 px-2 py-0.5 text-xs font-mono text-orange-700"
                    >
                      {range}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="text-gray-500">—</span>
              )}
            </dd>
          </div>
          {finding.description && (
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Descripción
              </dt>
              <dd className="mt-0.5 text-sm text-gray-900">{finding.description}</dd>
            </div>
          )}
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Evaluado
            </dt>
            <dd className="mt-0.5 text-sm text-gray-900">
              {new Date(finding.evaluatedAt).toLocaleString()}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Consola
            </dt>
            <dd className="mt-0.5 text-sm">
              {finding.consoleUrl ? (
                <a
                  href={finding.consoleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1"
                >
                  Abrir recurso en GCP Console
                  <ExternalLinkIcon />
                </a>
              ) : (
                <span className="text-gray-500">No disponible</span>
              )}
            </dd>
          </div>
        </dl>
      </section>

      {/* Remediations */}
      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Remediaciones</h2>
        {finding.remediations.length === 0 ? (
          <div className="rounded-md bg-gray-50 p-4 text-center">
            <p className="text-sm text-gray-600">Remediación no disponible</p>
          </div>
        ) : (
          <div className="space-y-4">
            {finding.remediations.map((remediation) => (
              <RemediationCard
                key={remediation.id}
                remediation={remediation}
                completing={completingId === remediation.id}
                onMarkCompleted={handleMarkCompleted}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* ─── Sub-Components ──────────────────────────────────────────────────────── */

function RemediationCard({
  remediation,
  completing,
  onMarkCompleted,
}: {
  remediation: Remediation;
  completing: boolean;
  onMarkCompleted: (r: Remediation) => void;
}) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-3">
      {/* Header with status */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-900">{remediation.description}</p>
        {remediation.status === 'completed' ? (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
            Completada
          </span>
        ) : (
          <button
            onClick={() => onMarkCompleted(remediation)}
            disabled={completing}
            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {completing ? 'Marcando…' : 'Marcar como completada'}
          </button>
        )}
      </div>

      {/* Risk Description */}
      <div>
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Descripción del Riesgo
        </h4>
        <p className="mt-1 text-sm text-gray-700">{remediation.riskDescription}</p>
      </div>

      {/* Ordered Steps */}
      <div>
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Pasos
        </h4>
        <ol className="mt-1 list-decimal list-inside space-y-1">
          {remediation.steps.map((step, index) => (
            <li key={index} className="text-sm text-gray-700">
              {step}
            </li>
          ))}
        </ol>
      </div>

      {/* Console Deep Link */}
      <div>
        {remediation.consoleUrl ? (
          <a
            href={remediation.consoleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <ExternalLinkIcon />
            Abrir en consola
          </a>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <ExternalLinkIcon />
            Enlace no disponible
          </span>
        )}
      </div>
    </div>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  );
}
